import { Injectable, inject } from '@angular/core';
import { compose, Store } from '@ngrx/store';
import { selectTechnicians } from '../../../../store/gse/inventory/lookups/technician/technician.selectors';
import * as TechnicianActions from '../../../../store/gse/inventory/lookups/technician/technician.actions';
import { SparePartItem } from '../../../../features/gse/reports/types/report.types';
import { InventorySparePartReportDto } from '../../../../api/gse/models/inventory-spare-part-report-dto';
import { EquipmentTATItem } from '../../../../features/gse/reports/mock/equipment-tat-page1';
import { MaintenanceHistoryItem } from '../../../../features/gse/reports/mock/maintenance-history-page1';
import { ProcurementItem } from '../../../../features/gse/reports/mock/procurement-page1';
import { JobCardSummaryItem } from '../../../../features/gse/reports/mock/job-card-summary-page1';
import { BackJobItem } from '../../../../features/gse/reports/mock/back-job-page1';
import { BackJobReportEntry } from '../../../../api/gse/models/back-job-report-entry';
import { TechnicianEfficiencyItem } from '../../../../features/gse/reports/mock/technician-efficiency-page1';
import { EquipmentServiceabilityItem } from '../../../../features/gse/reports/mock/equipment-serviceability-page1';
import { EquipmentServiceabilityReportEntry } from '../../../../api/gse/models/equipment-serviceability-report-entry';
import { EquipmentDowntimeItem } from '../../../../features/gse/reports/mock/equipment-downtime-page1';
import { PMComplianceItem } from '../../../../features/gse/reports/mock/pm-compliance-page1';
import { PmComplianceReportEntry } from '../../../../api/gse/models/pm-compliance-report-entry';
import { TechnicianEfficiencyReportEntry } from '../../../../api/gse/models/technician-efficiency-report-entry';
import { DelegationItem } from '../../../../features/gse/reports/mock/delegation-page1';
import { DelegationReportDto } from '../../../../api/administration/models/delegation-report-dto';
import { UserMasterListItem } from '../../../../features/gse/reports/mock/user-master-list-page1';
import { UserReportResponseDto } from '../../../../api/administration/models/user-report-response-dto';
import { InventoryMaintenanceHistoryReportDto } from '../../../../api/gse/models/inventory-maintenance-history-report-dto';
import { InventoryProcurementReportDto } from '../../../../api/gse/models/inventory-procurement-report-dto';
import { JobCardTatReportEntry } from '../../../../api/gse/models/job-card-tat-report-entry';
import { EquipmentDowntimeReportEntry } from '../../../../api/gse/models/equipment-downtime-report-entry';
import { EquipmentTatReportEntry } from '../../../../api/gse/models/equipment-tat-report-entry';

export type ReportItem =
  | SparePartItem
  | EquipmentTATItem
  | MaintenanceHistoryItem
  | InventoryMaintenanceHistoryReportDto
  | ProcurementItem
  | InventoryProcurementReportDto
  | JobCardSummaryItem
  | JobCardTatReportEntry
  | BackJobItem
  | BackJobReportEntry
  | TechnicianEfficiencyItem
  | TechnicianEfficiencyReportEntry
  | EquipmentServiceabilityItem
  | EquipmentServiceabilityReportEntry
  | EquipmentDowntimeItem
  | EquipmentDowntimeReportEntry
  | EquipmentTatReportEntry
  | PMComplianceItem
  | PmComplianceReportEntry
  | DelegationItem
  | DelegationReportDto
  | UserMasterListItem
  | UserReportResponseDto;

export interface ReportTableRow {
  [key: string]: string | number;
}

type DataMapperFn<T> = (item: T) => ReportTableRow;

@Injectable({
  providedIn: 'root',
})
export class ReportDataMapperService {
  private store = inject(Store);
  private techniciansCache: Array<{ id: number; name: string }> = [];

  constructor() {
    this.loadTechnicians();
  }

  private loadTechnicians(): void {
    this.store.dispatch(TechnicianActions.loadTechnicians());
    this.store.select(selectTechnicians).subscribe((technicians) => {
      this.techniciansCache = technicians || [];
    });
  }

  getTechnicianNamesByIds(technicianIdsString: string): string {
    if (!technicianIdsString) return '';

    const ids = technicianIdsString
      .split(',')
      .map((id) => id.trim())
      .filter((id) => id)
      .map((id) => Number(id));

    if (ids.length === 0) return '';
    const names = ids
      .map((id) => this.techniciansCache.find((t) => t.id === id))
      .filter((t) => t !== undefined)
      .map((t) => t!.name);

    return names.join(', ');
  }

  private mappers: Record<string, DataMapperFn<ReportItem>> = {
    'spare-parts': this.mapSparePartToRow.bind(this) as DataMapperFn<ReportItem>,
    'equipment-tat': this.mapEquipmentTatToRow.bind(this) as DataMapperFn<ReportItem>,
    'maintenance-history': this.mapMaintenanceHistoryToRow.bind(this) as DataMapperFn<ReportItem>,
    procurement: this.mapProcurementToRow.bind(this) as DataMapperFn<ReportItem>,
    'job-card-summary': this.mapJobCardSummaryToRow.bind(this) as DataMapperFn<ReportItem>,
    'back-job': this.mapBackJobToRow.bind(this) as DataMapperFn<ReportItem>,
    'technician-efficiency': this.mapTechnicianEfficiencyToRow.bind(
      this
    ) as DataMapperFn<ReportItem>,
    'equipment-serviceability': this.mapEquipmentServiceabilityToRow.bind(
      this
    ) as DataMapperFn<ReportItem>,
    'equipment-downtime': this.mapEquipmentDowntimeToRow.bind(this) as DataMapperFn<ReportItem>,
    'pm-compliance': this.mapPMComplianceToRow.bind(this) as DataMapperFn<ReportItem>,
    delegation: this.mapDelegationToRow.bind(this) as DataMapperFn<ReportItem>,
    'user-master-list': this.mapUserMasterListToRow.bind(this) as DataMapperFn<ReportItem>,
  };

  mapDataToRow(item: ReportItem, reportType: string): ReportTableRow {
    const mapper = this.mappers[reportType];
    if (!mapper) {
      console.warn(`No mapper found for report type: ${reportType}`);
      return item as unknown as ReportTableRow;
    }
    return mapper(item);
  }

  private mapSparePartToRow(item: SparePartItem | InventorySparePartReportDto): ReportTableRow {
    const isDto = 'sparePartName' in item;

    if (isDto) {
      const dto = item as InventorySparePartReportDto;
      return {
        sparePartName: dto.sparePartName || '',
        category: dto.category || '',
        brand: dto.brand || '',
        modelNumber: dto.modelNumber || '',
        currentStock: dto.currentStock || 0,
        minQty: dto.minQuantity || 0,
        usage: dto.usage !== undefined ? Number(dto.usage.toFixed(1)) : 0,
      };
    }

    const mock = item as SparePartItem;
    const usage = mock.minStock > 0 ? (mock.stock / mock.minStock) * 100 : 0;
    return {
      sparePartName: mock.name,
      category: mock.category.name,
      brand: mock.brand.name,
      modelNumber: mock.modelNo || '',
      currentStock: mock.stock,
      minQty: mock.minStock,
      usage: Number(usage.toFixed(1)),
    };
  }

  private mapEquipmentTATToRow(item: EquipmentTATItem): ReportTableRow {
    return {
      startDate: item.startDate,
      endDate: item.endDate,
      manufacturer: item.manufacturer.name,
      equipmentId: item.equipmentId,
      model: item.model.name,
      serviceType: item.serviceType,
      idleTime: item.idleTimeHours,
      technicians: item.technicians.join(', '),
    };
  }

  private mapMaintenanceHistoryToRow(
    item: MaintenanceHistoryItem | InventoryMaintenanceHistoryReportDto
  ): ReportTableRow {
    const isDto = 'equipmentName' in item;

    if (isDto) {
      const dto = item as InventoryMaintenanceHistoryReportDto;
      return {
        jobCardId: dto.jobCardId || '',
        startDate: dto.startDate || '',
        endDate: dto.endDate || '',
        equipmentName: dto.equipmentName || '',
        maintenanceType: dto.maintenanceType || '',
        spareParts: (dto.spareParts || []).join(', '),
        technician: this.getTechnicianNamesByIds((dto.technicians || []).join(',')),
      };
    }

    const mock = item as MaintenanceHistoryItem;
    return {
      jobCardId: mock.jobCardId,
      startDate: mock.startDate,
      endDate: mock.endDate,
      equipmentName: mock.equipment.name,
      maintenanceType: mock.maintenanceType,
      spareParts: mock.spareParts.join(', '),
      technician: mock.technician.name,
    };
  }

  private mapProcurementToRow(
    item: ProcurementItem | InventoryProcurementReportDto
  ): ReportTableRow {
    const isDto = 'pettyCashEnabled' in item || 'purchaseRequestId' in item;

    if (isDto) {
      const dto = item as InventoryProcurementReportDto;
      return {
        purchaseReqId: dto.purchaseRequestId || '',
        prDate: dto.prDate || '',
        spareParts: (dto.spareParts || []).join(', '),
        vendors: (dto.vendors || []).join(', '),
        pettyCash: dto.pettyCashEnabled ? 'Yes' : 'No',
      };
    }

    const mock = item as ProcurementItem;
    return {
      purchaseReqId: mock.purchaseReqId,
      prDate: mock.prDate,
      spareParts: mock.spareParts.join(', '),
      vendors: mock.vendors.join(', '),
      pettyCash: mock.pettyCash ? 'Yes' : 'No',
    };
  }

  private mapJobCardSummaryToRow(item: JobCardSummaryItem | JobCardTatReportEntry): ReportTableRow {
    // Check if it's the API response (has equipmentName field)
    const isApiEntry = 'equipmentName' in item;

    if (isApiEntry) {
      const apiEntry = item as JobCardTatReportEntry;
      return {
        jobCardId: apiEntry.jobCardId || '',
        equipmentName: apiEntry.equipmentName || '',
        startDate: apiEntry.startDate || '',
        endDate: apiEntry.endDate || '',
        priority: apiEntry.priority || '',
        tat: apiEntry.tat !== undefined ? Number((apiEntry.tat / 3600).toFixed(2)) : 0,
        status: apiEntry.status || '',
      };
    }

    const mock = item as JobCardSummaryItem;
    return {
      jobCardId: mock.jobCardId,
      equipmentName: mock.equipment.name,
      startDate: mock.startDate,
      endDate: mock.endDate || 'N/A',
      priority: mock.priority,
      tat: mock.tat,
      status: mock.status,
    };
  }

  private mapBackJobToRow(item: BackJobItem | BackJobReportEntry): ReportTableRow {
    const equipmentName =
      typeof item.equipment === 'string'
        ? item.equipment
        : (item as BackJobItem).equipment?.name || '';
    return {
      jobCardId: item.jobCardId || '',
      equipment: equipmentName,
      backJobCardId: item.backJobCardId || '',
      status: item.status || '',
      reason: item.reason || '',
    };
  }

  private mapTechnicianEfficiencyToRow(
    item: TechnicianEfficiencyItem | TechnicianEfficiencyReportEntry
  ): ReportTableRow {
    const isDto = 'actualSeconds' in item;

    if (isDto) {
      const dto = item as TechnicianEfficiencyReportEntry;
      return {
        jobCardId: dto.jobCardId || '',
        jobType: dto.jobType || '',
        // The API returns seconds, but the UI expects format mapped to "Hours" headers.
        estimatedHours: dto.estimatedSeconds ? Number((dto.estimatedSeconds / 3600).toFixed(2)) : 0,
        actualHours: dto.actualSeconds ? Number((dto.actualSeconds / 3600).toFixed(2)) : 0,
        efficiencyPercentage:
          dto.efficiencyPercentage !== undefined ? Number(dto.efficiencyPercentage.toFixed(1)) : 0,
      };
    }

    const mock = item as TechnicianEfficiencyItem;
    return {
      jobCardId: mock.jobCardId,
      jobType: mock.jobType,
      estimatedHours: mock.estimatedHours,
      actualHours: mock.actualHours,
      efficiencyPercentage: Number(mock.efficiencyPercentage.toFixed(1)),
    };
  }

  private mapEquipmentServiceabilityToRow(item: any): ReportTableRow {
    const equipmentId = item.idNumber || item.equipmentId || '';
    const equipmentName = item.equipment || '';
    const category = item.equipmentCategory || '';
    const equipmentType = item.equipmentType || '';
    const model = item.model || '';

    return {
      equipmentId: equipmentId,
      equipmentName: equipmentName,
      category: category,
      equipmentType: equipmentType,
      model: model,
      serviceability: item.serviceability || '',
    };
  }

  private mapEquipmentDowntimeToRow(
    item: EquipmentDowntimeItem | EquipmentDowntimeReportEntry
  ): ReportTableRow {
    // Check if it's the API response (has downTimeReason field)
    const isApiEntry = 'downTimeReason' in item;

    if (isApiEntry) {
      const apiEntry = item as EquipmentDowntimeReportEntry;
      return {
        startDate: apiEntry.startDate || '',
        endDate: apiEntry.endDate || '',
        totalHours:
          apiEntry.totalSeconds !== undefined
            ? Number((apiEntry.totalSeconds / 3600).toFixed(2))
            : 0,
        downTimeReason: apiEntry.downTimeReason || '',
      };
    }

    const mock = item as EquipmentDowntimeItem;
    return {
      startDate: mock.startDate,
      endDate: mock.endDate,
      totalHours: mock.totalHours,
      downTimeReason: mock.downtimeReason,
    };
  }

  private mapPMComplianceToRow(item: PMComplianceItem | PmComplianceReportEntry): ReportTableRow {
    const equipmentName = (item as PmComplianceReportEntry).equipment || '';
    const plannedPM = item.plannedPM || 0;
    const completedPM = item.completedPM || 0;
    const pmCompliancePercentage =
      (item as PMComplianceItem).pmCompliancePercentage !== undefined
        ? (item as PMComplianceItem).pmCompliancePercentage
        : (item as PmComplianceReportEntry).compliancePercentage || 0;

    return {
      equipmentName: equipmentName,
      plannedPM: plannedPM,
      completedPM: completedPM,
      pmCompliancePercentage: Number(pmCompliancePercentage.toFixed(1)),
    };
  }

  private mapEquipmentTatToRow(item: EquipmentTatReportEntry): ReportTableRow {
    return {
      startDate: item.startDate || '',
      endDate: item.endDate || '',
      manufacturer: item.manufacture || '',
      equipmentId: item.equipmentId || '',
      model: item.model || '',
      serviceType: item.serviceType || '',
      idleTime: item.idleTimeSeconds !== undefined ? Number(item.idleTimeSeconds) : 0,
      technicians: item.technicians || '',
    };
  }

  private mapDelegationToRow(item: DelegationReportDto): ReportTableRow {
    return {
      startDate: item.startDate || '',
      endDate: item.endDate || '',
      delegator: item.delegatorName || '',
      status: item.status || '',
      delegate: item.delegateName || '',
      taskCount: item.taskCount ?? 0,
    };
  }

  private mapUserMasterListToRow(item: UserReportResponseDto): ReportTableRow {
    return {
      name: item.fullName || '',
      email: item.email || '',
      designation: item.designation || '',
      userStatus: item.status || '',
    };
  }

  registerMapper<T extends ReportItem>(reportType: string, mapper: DataMapperFn<T>): void {
    this.mappers[reportType] = mapper as DataMapperFn<ReportItem>;
  }
}
